import { Room } from 'livekit-client';
import type { ModerationCommand } from '../types';
import { MODERATION_DATA_TOPIC } from '../types';
import type { ModeratorAction } from '../constants';

/**
 * Публикует команду модерации широковещательно (всем участникам).
 * Используется для START_WEBINAR / END_WEBINAR.
 * Mobile ждёт targetSid в payload - для webinar broadcast кладём туда moderatorSid.
 */
export const publishBroadcastCommand = async (room: Room | null, action: ModeratorAction): Promise<boolean> => {
    if (!room) {
        console.warn('[Moderation] broadcast не отправлен: room отсутствует', { action });
        return false;
    }

    const localParticipant = room.localParticipant;
    if (!localParticipant?.sid) {
        console.warn('[Moderation] broadcast не отправлен: нет sid локального участника', { action });
        return false;
    }

    const moderatorSid = localParticipant.sid;
    const moderatorIdentity = localParticipant.identity || undefined;

    const cmd: ModerationCommand = {
        action,
        moderatorSid,
        moderatorIdentity,
        // Mobile парсит START/END_WEBINAR как обычную moderation команду и требует targetSid
        targetSid: moderatorSid,
        targetIdentity: moderatorIdentity,
    };

    try {
        const data = new TextEncoder().encode(JSON.stringify(cmd));
        await localParticipant.publishData(data, { reliable: true, topic: MODERATION_DATA_TOPIC });
        console.log('[Moderation] broadcast отправлен', cmd);
        return true;
    } catch (error) {
        console.error('[Moderation] ошибка broadcast команды', { error, action, cmd });
        return false;
    }
};

/**
 * Публикует команду модерации через LiveKit data channel.
 *
 * @param room - LiveKit room (может быть null)
 * @param action - действие модерации для выполнения
 * @param target - цель команды:
 *   - sid: LiveKit participant SID,меняться при переподключении
 *   - identity: стабильна при переподключении, сервер предпочитает identity для сопоставления
 * @returns true если команда была успешно опубликована, false в противном случае
 */
export const publishModerationCommand = (
    room: Room | null,
    action: ModeratorAction,
    target: { sid: string; identity?: string | null },
): boolean => {
    if (!room) {
        console.warn('[Moderation] не удалось опубликовать команду: отсутствует room');
        return false;
    }

    const localParticipant = room.localParticipant;
    if (!localParticipant) {
        console.warn('[Moderation] не удалось опубликовать команду: отсутствует localParticipant');
        return false;
    }

    const moderatorSid = localParticipant.sid;
    if (!moderatorSid) {
        console.warn('[Moderation] не удалось опубликовать команду: отсутствует SID локального участника');
        return false;
    }

    const targetSid = target.sid?.trim();
    if (!targetSid) {
        console.error('[Moderation] не удалось опубликовать команду: отсутствует SID таргетированного участника', {
            action,
            target,
        });
        return false;
    }

    const cmd: ModerationCommand = {
        action,
        targetSid,
        targetIdentity: target.identity || undefined,
        moderatorSid,
        moderatorIdentity: localParticipant.identity || undefined,
    };

    try {
        const data = new TextEncoder().encode(JSON.stringify(cmd));
        console.log('cmd', cmd);
        localParticipant.publishData(data, { reliable: true, topic: MODERATION_DATA_TOPIC });
        return true;
    } catch (error) {
        console.error('[Moderation] ошибка в публикации команды', { error, cmd });
        return false;
    }
};
